import React, {useEffect, useState} from 'react';
import {Sidebar} from "primereact/sidebar";
import {Button} from "primereact/button";
import Icon from "../../../system/components/icon";
import {getFileSize} from "../../../system/utils";
import {useDispatch, useSelector} from "react-redux";
import {useTranslate} from "../../../system/useTranslate";
import Span from "../../../system/components/span";
import {AutoComplete} from "primereact/autocomplete";
import {setMessage} from "../../../system/app-slice";
import {copyWorkspaceMediaLink, downloadWorkspaceMedia} from "../../../system/private-media";
import WorkspaceMediaThumbnail from "../../../system/components/workspace-media-thumbnail";

function FileSidebar({visible, onHide, payload, onChange}) {
    const translate = useTranslate();
    const [fileInfo, setFileInfo] = useState(null);


    useEffect(()=>{
        if (payload?.selectedFile){
            setFileInfo({...getFileSize(payload?.selectedFile?.size), ...payload?.selectedFile})
        }
        else onHide?.();
    },[onHide, payload?.selectedFile])


    return (
        <Sidebar showCloseIcon={false} dismissable={false} modal={false} visible={visible} onHide={onHide} className="w-3 rtl headerless-sidebar">
            <div className="flex flex-column h-full">
                <Header file={payload?.selectedFile} onHide={onHide}/>
                <div className="flex-1 p-3 flex flex-column gap-2">
                    <DownloadBox fileInfo={fileInfo}/>
                    <span className="text-xs font-bold text-500">{translate("file-details")}</span>
                    <FileInfoBox fileInfo={fileInfo}/>
                    <span className="text-xs font-bold text-500">{translate("used-in")}</span>
                    <FileUsedIn fileInfo={fileInfo} tasks={payload?.tasks} onChange={onChange}/>
                </div>
                <Footer projectData={payload?.projectData} fileInfo={fileInfo} onChange={onChange}/>
            </div>
        </Sidebar>
    );
}

function DownloadBox({fileInfo}) {
    const translate = useTranslate();
    const dispatch = useDispatch();
    const workspaceId = useSelector((state) => state.app.workspaceId);
    return(
        <div className="border-round surface-50 gap-1 p-3 border-1 border-200 flex flex-column align-items-center justify-content-center">
            <div className="border-round-xl surface-0 border-1 border-200 p-3 shadow-1">
                {fileInfo && <WorkspaceMediaThumbnail file={fileInfo} workspaceId={workspaceId} width="4rem" preview/>}
            </div>

            <span className="text-xs font-bold mt-2">{fileInfo?.name}</span>
            <span className="text-xs text-300">{fileInfo?.size} {translate(fileInfo?.unit)}</span>
            <div className="flex align-items-center gap-2 w-full mt-3">
                <Button className="flex-1 text-xs p-2" label={translate("download")} onClick={()=>downloadWorkspaceMedia(fileInfo, workspaceId).catch(()=>undefined)} icon={<Icon icon="mi mi:download" className="text-lg"/>}/>
                <Button className="flex-1 text-xs p-2 p-button-info p-button-outlined" onClick={async ()=>{
                    try {
                        await copyWorkspaceMediaLink(fileInfo, workspaceId);
                        dispatch(setMessage({severity:"info", content:translate("file-path-copied")}));
                    } catch {
                        // The shared API interceptor displays the access error.
                    }
                }} label={translate("copy-link")} icon={<Icon icon="mi mi:link" className="text-lg"/>}/>
            </div>
        </div>
    )
}
function FileInfoBox({fileInfo}) {
    const translate = useTranslate();

    return(
        <div className="border-round border-1 border-200 flex flex-column align-items-center justify-content-center">
            <LabelValue className="hover:surface-100 border-round-top border-bottom-1 border-100 p-2" label={translate("file-type")}>
                <span className="text-xs font-bold">{fileInfo?.ext.replace(".","")}</span>
            </LabelValue>
            <LabelValue  className="hover:surface-100 border-bottom-1 border-100 p-2" label={translate("file-size")}>
                <span className="text-xs font-bold">{fileInfo?.size} {translate(fileInfo?.unit)}</span>
            </LabelValue>
            <LabelValue  className="hover:surface-100 border-bottom-1 border-100 p-2" label={translate("uploaded-by")}>
                <span className="text-xs font-bold"></span>
            </LabelValue>
            <LabelValue  className="hover:surface-100 border-round-bottom p-2" label={translate("uploaded-at")}>
                <Span spanClassName="text-xs font-bold" format="YYYY/MM/DD - HH:mm" type="PERSIAN_DATE">{fileInfo?.createdAt}</Span>
            </LabelValue>
        </div>
    )
}
function FileUsedIn({fileInfo, tasks=[], onChange}) {
    const translate = useTranslate();
    const [filteredTasks, setFilteredTasks] = useState(null);
    const [selectedTasks, setSelectedTasks] = useState([]);
    const [removedTasks, setRemovedTasks] = useState([]);
    const [searchTasksVisible, setSearchTasksVisible] = useState(false);
    useEffect(() => {
        setSelectedTasks(fileInfo?.related?.filter(f=>f.__type==="api::task.task"))
    }, [fileInfo, searchTasksVisible]);
    const search = (event) => {
        // Timeout to emulate a network connection
        setTimeout(() => {
            let _filteredTasks;

            if (!event.query.trim().length) {
                _filteredTasks = [...tasks];
            }
            else {
                _filteredTasks = tasks.filter((task) => {
                    return task.title.toLowerCase().startsWith(event.query.toLowerCase());
                });
            }
            setFilteredTasks(_filteredTasks);
        }, 250);
    }


    return(
        <div className="flex flex-column gap-2 align-items-center justify-content-center">
            {
                fileInfo.related.filter(r=>r.__type==="api::task.task").map(r=>(
                    <div className="w-full border-round border-1 border-300 p-2 px-3 text-cyan-500 flex justify-content-between align-items-center">
                        <span className="text-xs">{r.title}</span>
                        <Icon icon="mi mi:link_off" onClick={()=>{onChange?.({removedTasks:[r], selectedTasks:[] ,fileInfo, linkTo:"task"})}}/>
                    </div>
                ))
            }
            {searchTasksVisible?(
                <div className={`w-full border-round surface-50 border-1 border-200 p-2 flex flex-column gap-2`}>
                    <span className="text-2xs text-400 ">{translate("search-task")}</span>
                    <AutoComplete placeholder={translate("search-task-placeholder")}
                                  onUnselect={(e)=>{setRemovedTasks([...removedTasks, e.value])}} panelClassName="rtl" inputClassName="shabnam-font text-xs" multiple field="title" suggestions={filteredTasks} completeMethod={search} value={selectedTasks} onChange={(e) => setSelectedTasks(e.value)} className="w-full rtl" pt={{item:()=>({className:"text-xs p-2"}),token:()=>({className:"text-xs gap-2 pl-0 m-0"}),loadingIcon:()=>({style:{left:"0.75rem"}}), container:()=>({className:"w-full px-2 gap-2"})}}/>
                    <div className="flex gap-2 align-items-center">
                        <Button icon={<Icon icon="mi mi:check" className="text-lg"/>} className="w-full text-xs p-2" onClick={()=>{onChange?.({selectedTasks, removedTasks, fileInfo, linkTo:"task"}); setSearchTasksVisible(false)}} />
                        <Button icon={<Icon icon="mi mi:close" className="text-lg"/>} className="text-xs p-2 p-button-secondary p-button-outlined" onClick={()=>{setSearchTasksVisible(false)}} />
                    </div>
                </div>
            ):(
                <Button onClick={()=>{setSearchTasksVisible(true)}} icon={<Icon icon="mi mi:add" className="text-lg"/>} className="w-full text-xs" label={translate("add-to-other-tasks")}/>
            )}

        </div>
    )
}



function LabelValue({label, children, className}) {
    return(
        <div className={`w-full flex flex-column gap-2 ${className}`}>
            <div className="flex align-items-center gap-2 justify-content-between">
                <span className="text-xs text-400 w-6rem">{label}</span>
                {children??<></>}
            </div>
        </div>
    )
}
function Header({file, onHide}) {
    const translate = useTranslate();
    const workspaceId = useSelector((state) => state.app.workspaceId);

    return(
        <div className="flex justify-content-between align-items-center p-2 border-bottom-1 border-200">
            {file&&(
                <div className="flex align-items-center">
                    <WorkspaceMediaThumbnail file={file} workspaceId={workspaceId} width="2.5rem"/>
                    <div className="flex flex-column text-xs ">
                        <span className="font-bold">{file.name}</span>
                        <div className="flex align-items-centergap-2 text-2xs text-400">
                            <span>{translate("uploaded-at")}:</span>
                            <Span type="PERSIAN_DATE" format="YYYY/MM/DD">{file.createdAt}</Span>
                        </div>
                    </div>
                </div>
            )}
            <Icon icon="mi mi:close" onClick={()=>{onHide?.()}}/>
        </div>
    )
}


function Footer({fileInfo, projectData, onHide, onChange}) {
    const translate = useTranslate();
    let isAttachedToProject = fileInfo.related.find(r=>r.__type==="api::project.project"&&r.id===projectData?.id)

    return(
        <div className="flex justify-content-between align-items-center p-2 border-top-1 border-200">
            <Button onClick={()=>{onChange?.({fileInfo, linkTo:"project"})}} icon={<Icon icon={`${isAttachedToProject?"mi mi:delete":"mi mi:link"}`}/>} className={`p-button-outlined ${isAttachedToProject?"p-button-primary":"p-button-info"} text-xs w-full`} label={translate(isAttachedToProject?"delete-from-project":"attach-to-project")}/>
        </div>
    )
}


export default FileSidebar;
